import _ from "lodash";
import i18next from "src/i18n/config";
import { makeStyles } from "tss-react/mui";

import { Theme } from "@mui/material";
import { CustomBadge } from "@shared/CustomBadge";
import { Tooltip } from "@shared/Tooltip";

const useStyles = makeStyles()((theme: Theme) => ({
    artifact: {
        display: "inline-flex",
        alignItems: "center",
        color: theme.palette.emteria.indicator,
    },
    artifactIcon: {
        fontSize: theme.typography.pxToRem(18),
        lineHeight: 1,
    },
    artifactBadge: {
        display: "inline",
    },
    tooltipContent: {
        display: "flex",
        flexDirection: "column",
        fontSize: theme.typography.pxToRem(14),
        padding: theme.spacing(1),
    },
    tooltipList: {
        margin: theme.spacing(1, 0, 0),
        paddingLeft: theme.spacing(2),
    },
}));

export const getArtifactIcon = (artifact: API.BuildArtifact) => {
    switch (artifact.type) {
        case "ota":
            return {
                icon: "fa-solid fa-cloud-arrow-down",
                tooltip: i18next.t("pages:productPage.buildsTable.artifactTooltip.ota"),
            };
        case "os-firmware":
            return {
                icon: "fa-solid fa-microchip",
                tooltip: i18next.t("pages:productPage.buildsTable.artifactTooltip.osFirmware"),
            };
        case "signed-files":
            return {
                icon: "fa-solid fa-file-signature",
                tooltip: i18next.t("pages:productPage.buildsTable.artifactTooltip.signedFiles"),
            };
        case "manifest":
            return {
                icon: "fa-solid fa-file-lines",
                tooltip: i18next.t("pages:productPage.buildsTable.artifactTooltip.manifest"),
            };
        case "report":
            return {
                icon: "fa-solid fa-newspaper",
                tooltip: i18next.t("pages:productPage.buildsTable.artifactTooltip.report"),
            };
        case "metadata":
            return {
                icon: "fa-solid fa-file-code",
                tooltip: i18next.t("pages:productPage.buildsTable.artifactTooltip.metadata"),
            };
        default:
            return {
                icon: "fa-solid fa-certificate",
                tooltip: artifact.type,
            };
    }
};

const artifactsOrder = ["os-firmware", "ota", "signed-files", "manifest", "report", "metadata"];

type ArtifactType = API.BuildArtifact["type"];

export const Artifacts = (props: { artifacts: API.BuildArtifact[] }) => {
    const { classes } = useStyles();
    const uploadedArtifacts = props.artifacts.filter((item) => item.isUploaded);
    // Sort by name first, then stably by type, so the names listed in each type's tooltip keep a predictable order
    const namedArtifacts = [...uploadedArtifacts].sort((a, b) =>
        (a.name ?? "").localeCompare(b.name ?? "", undefined, { numeric: true, sensitivity: "base" }),
    );
    const orderedArtifacts = _.sortBy(namedArtifacts, (item) => artifactsOrder.indexOf(item.type));
    const artifactsByType = orderedArtifacts.reduce<Partial<Record<ArtifactType, API.BuildArtifact[]>>>((acc, artifact) => {
        const artifactArray = acc[artifact.type] ?? [];
        artifactArray.push(artifact);
        acc[artifact.type] = artifactArray;
        return acc;
    }, {});
    const uniqueArtifactsByType = _.uniqBy(orderedArtifacts, "type");

    return (
        <>
            {uniqueArtifactsByType.map((artifactOfType) => {
                const artifactsGroup = artifactsByType[artifactOfType.type];
                if (!artifactsGroup?.length) {
                    return null;
                }
                const { icon, tooltip } = getArtifactIcon(artifactOfType);
                const tooltipContent = (
                    <div className={classes.tooltipContent}>
                        <div>{tooltip}</div>
                        <ul className={classes.tooltipList}>
                            {artifactsGroup.map((artifact, index) => (
                                <li key={`${artifact.type}-${artifact.id}-${index}`}>{artifact.name ?? ""}</li>
                            ))}
                        </ul>
                    </div>
                );

                return (
                    <div key={artifactOfType.type} className={classes.artifact}>
                        <Tooltip title={tooltipContent}>
                            <CustomBadge
                                badgeContent={artifactsGroup.length}
                                invisible={artifactsGroup.length <= 1}
                                className={classes.artifactBadge}
                                overlap="rectangular"
                            >
                                <i className={`${icon} ${classes.artifactIcon}`} />
                            </CustomBadge>
                        </Tooltip>
                    </div>
                );
            })}
        </>
    );
};
